/**
 * LEAD360-DEDUP-V1 (V1-P3) -- THE canonical Lead 360 detail component.
 * LEAD-360-CONFORMANCE-B2 (G1) -- the RECORD-PAGE archetype's reference
 * implementation (docs/design/design-language/07-BEHAVIOURAL-LAWS.md):
 * compact masthead -> lifecycle line -> NEXT band (composer on invocation)
 * -> a TIMELINE-DOMINANT center column -> a counted expand-in-place rail.
 *
 * The single source of truth for a lead's full record view. Both the
 * canonical full-page route (`/dashboard/leads/:safeLeadId`,
 * LeadsWorkspaceDetailPage) and the owner detail route's kill-switch render
 * compose THIS component; no surface renders its own lead-360 body.
 *
 * RECORD GRAMMAR (the RECORD-PAGE archetype; reusable for Patient 360, the
 * COC opportunity record, and Money record pages by CITATION, not re-
 * derivation):
 *   1. breadcrumbs + compact masthead (RecordHeaderBand -- identity + chips
 *      + one-line meta + at most ONE emphasized header action ("Book
 *      appointment", exactly when the canonical rule permits booking) +
 *      the overflow menu; LEAD-CONTACT-FLOW-V1 deleted the separate
 *      Reveal link -- the audited reveal lives in the NEXT card);
 *   2. the STATUS PATH BAR (LeadStagePathBar -- the existing stage model,
 *      read-only, no stage writer);
 *   3. the NEXT band (ui/NextActionEdge) -- ONE loud sentence + the
 *      workflow it names, launched IN PLACE (NEXT-IS-EXECUTABLE,
 *      LEAD-CONTACT-FLOW-V1): the contact workflow's channel chooser ->
 *      audited reveal + server-granted countdown -> disposition advance;
 *      or the booking MODAL; or the rail anchor for manage/reassign. The
 *      composer opens from the workflow or the overflow menu's "Add note
 *      / record offline touch" (the demoted Log activity);
 *   4. MAIN COLUMN: the PROMOTED activity timeline (ui/Timeline) -- the
 *      page's heart, filterable, latest-accented, routine-dimmed -- then
 *      the honesty footer. No card stack: every other legacy card
 *      (Contact/Details/Related/Lead History) is dispositioned BY NAME in
 *      the LEAD-360-CONFORMANCE-B2 run record -- relocated to the rail,
 *      the masthead, or the timeline itself, or retired with its pins
 *      (R-015 ONE-ACTIVE-CONTRACT) when the approved frames draw it no
 *      home.
 *   5. RIGHT RAIL: counted, expand-in-place SectionContainers in per-state
 *      order (Owner / Lead details / Campaign and source / Appointments /
 *      Patient record).
 *
 * PURE + PHI-BOUNDED: this module adds no data, fetches no PHI, and
 * reinterprets nothing -- every value renders from the role-projected
 * `Lead360Input` through the existing resolvers (current-truth, the stage
 * rail model, the last-attempt projection). The lead record's audited
 * reveal happens through the ONE contact-workflow server action inside
 * the NEXT card (LEAD-CONTACT-FLOW-V1; the CONTACT-REVEAL-AUDIT-01
 * one-mount discipline continues -- one reveal door on this surface, the
 * plaintext held only in transient workflow state and dropped on
 * re-mask). Role projection is enforced server-side (restricted-section
 * field values are ABSENT, not null); this renders only the sections the
 * payload carries.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { recordPrevisitDisposition, startLeadContactWorkflow } from '@/dashboard/api';
import {
  SectionIcon,
  formatTimestampIst,
} from '@/dashboard/owner/Lead360Panel';
import { findActiveAppointmentCard } from '@/dashboard/owner/AppointmentWorkbench';
import {
  ContactWorkflowChooser,
  ContactWorkflowPanelView,
} from '@/dashboard/leads/LeadContactWorkflow';
import {
  ADVANCE_HEADING,
  CONTACT_WORKFLOW_IDLE,
  contactStateAfterError,
  contactStateAfterStart,
  contactStateMasked,
  contactStateStarting,
  contactWorkflowInvoked,
  type ContactChannelChoice,
  type ContactWorkflowUiState,
} from '@/dashboard/leads/lead-contact-workflow-view';
import {
  buildLead360View,
  isLead360SectionVisible,
  type Lead360Event,
  type Lead360EventStreamStatus,
  type Lead360Input,
} from '@/dashboard/owner/lead-360-view';
import { formatLeadRef } from '@/dashboard/owner/leads-object-view';
import {
  buildStageRailModel,
  LEAD_STAGE_RANK,
  NEXT_ACTION_UNKNOWN_LABEL,
} from '@/dashboard/leads/lead-lifecycle-rail-view';
import {
  appointmentDerivedNextAction,
  deriveAppointmentMilestone,
  DERIVED_MILESTONE_LABEL,
  DERIVED_MILESTONE_TAG,
} from '@/dashboard/leads/appointment-milestone-view';
import { resolveLeadRecordCurrentTruth } from '@/dashboard/record/current-truth';
import {
  AppointmentStatusChip,
  CurrentStateBand,
} from '@/dashboard/record/RecordHeaderBand';
import AppointmentZone, {
  PaymentRelatedList,
} from '@/dashboard/record/AppointmentZone';
import LeadStagePathBar from '@/dashboard/leads/LeadStagePathBar';
import ReturningPatientPanel from '@/dashboard/leads/ReturningPatientPanel';
import SlaBreachPanel from '@/dashboard/leads/SlaBreachPanel';
import { LeadOwnerControlsView } from '@/dashboard/leads/LeadOwnerControls';
import { resolveLastContactAttempt } from '@/dashboard/leads/last-contact-attempt';
import {
  ACTION_TARGET_APPOINTMENTS,
  ACTION_TARGET_OWNERSHIP,
  buildLeadActionBar,
  buildLeadDetailFields,
  buildLeadDetailsRailFields,
  buildLeadSummaryTiles,
  buildUpcomingItems,
  groupLeadDetailFields,
  resolveEmphasisActionKey,
  resolveOwnershipPlaneRow,
} from '@/dashboard/leads/lead-record-experience';
import type { BandChipKey } from '@/dashboard/record/RecordHeaderBand';
import {
  Breadcrumbs,
  ConfirmDialog,
  EmptyState,
  FieldGrid,
  ModalShell,
  OverflowMenu,
  PrimaryAction,
  SecondaryAction,
  type OverflowMenuItem,
} from '@/dashboard/ui';
// CHRONICLE-KIT-EXTRACTION-V1: the frozen RECORD-PAGE anatomy is assembled
// from the named AEDS kit (07-BEHAVIOURAL-LAWS.md "### ARCH" UPDATE 2 +
// "### KIT"). Each kit part composes the ONE shared ui/record primitive it
// names -- KitNextBand -> NextActionEdge, KitTimeline -> Timeline,
// KitRailSection -> SectionContainer, KitRecordMasthead -> RecordHeader.
import {
  KitNextBand,
  KitRailSection,
  KitRecordMasthead,
  KitRecordPage,
  KitTimeline,
} from '@/dashboard/kit';
import { buildLeadTimelineEntries } from '@/dashboard/leads/lead-record-experience';
import LeadConversationTranscript, {
  type LeadConversationView,
} from '@/dashboard/leads/LeadConversationTranscript';
import {
  leadLinkedRecords,
  leadRecordBreadcrumbs,
} from '@/dashboard/leads/lead-360-view';

/** Default back-link target (owner Leads list). Callers override per surface. */
const BACK_TO_LEADS = '/dashboard/owner/leads';

/** Find a lead record by its opaque safe reference. Pure; returns null if absent. */
export function findLeadRecord(
  records: readonly Lead360Input[],
  safeLeadId: string,
): Lead360Input | null {
  return records.find((r) => r.safeLeadId === safeLeadId) ?? null;
}

export function BackLink({ to = BACK_TO_LEADS }: { to?: string }): JSX.Element {
  return (
    <Link
      to={to}
      className="ac-btn ac-btn--secondary ac-btn--sm"
      data-testid="lead-detail-back"
    >
      ← Back to leads
    </Link>
  );
}

/**
 * `lead360.stage.rail` (S1 section) -- LIFECYCLE-RENDER-V1 (V1-P2).
 *
 * RETAINED, NOT COMPOSED (CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1):
 * the vertical rail's presentation home is now the horizontal
 * LeadStagePathBar (the SAME buildStageRailModel truth); this component is
 * kept ONLY because lead360-stage-rail.test.tsx pins its engine-mirror
 * derivation (the NextActionCard precedent). The V7-P4 design pass sweeps
 * it with those pins. Do NOT re-compose it on a page.
 */
export function StageRailSection({ record }: { record: Lead360Input }): JSX.Element {
  const model = buildStageRailModel(record.stage, record.events);
  // TRUTH-DISPLAY LAW: surface the strongest downstream FACT (an active
  // appointment) as a DERIVED milestone, distinct + labelled derived, WITHOUT
  // touching the recorded stage. Only when the recorded stage has not yet caught
  // up to `appointment_booked` (else the ladder already shows it honestly).
  const milestone = deriveAppointmentMilestone(record.appointmentSection, Date.now());
  const showDerived =
    milestone !== null &&
    (model.currentStage === null ||
      LEAD_STAGE_RANK[model.currentStage] < LEAD_STAGE_RANK.appointment_booked);
  const derivedMilestone = showDerived ? (
    <p
      className="lead360-stagerail__derived-milestone"
      data-testid="lead360-stagerail-appointment-milestone"
      data-appointment-when={milestone.isFuture ? 'future' : 'past'}
    >
      <span className="lead360-stagerail__derived-label">
        {DERIVED_MILESTONE_LABEL}
      </span>{' '}
      <span className="status-chip status-chip--neutral" data-testid="lead360-stagerail-derived-tag">
        {DERIVED_MILESTONE_TAG}
      </span>
      <span className="lead360-muted">
        {' '}
        {formatTimestampIst(milestone.appointmentTime)}
        {milestone.isFuture
          ? ' -- recorded stage has not caught up yet.'
          : ' -- this appointment date has passed.'}
      </span>
    </p>
  ) : null;

  return (
    <section className="lead360-section" aria-label="Lifecycle stage">
      <h4 className="lead360-section__title">
        <SectionIcon name="journey" />
        Lifecycle stage
      </h4>
      {!model.known ? (
        <>
          <p className="lead360-muted" data-testid="lead360-stagerail-empty">
            Lifecycle stage not recorded yet.
          </p>
          {derivedMilestone}
        </>
      ) : (
        <>
          <ol
            className="lead360-stagerail"
            data-testid="lead360-stagerail"
            data-current-stage={model.currentStage ?? ''}
          >
            {model.ladder.map((node) => (
              <li
                className="lead360-stagerail__step"
                key={node.stage}
                data-stage={node.stage}
                data-state={node.state}
                data-active={node.active ? 'yes' : 'no'}
                data-derived={node.derived ? 'yes' : 'no'}
                aria-current={node.active ? 'step' : undefined}
              >
                <span className="lead360-stagerail__label">
                  {node.state === 'done' ? (
                    <span
                      className="lead360-stagerail__check"
                      aria-hidden="true"
                      data-testid="lead360-stagerail-check"
                    >
                      {'\u2713 '}
                    </span>
                  ) : null}
                  {node.label}
                </span>
                {node.derived ? (
                  <span
                    className="lead360-stagerail__meta"
                    data-testid="lead360-stagerail-derived"
                  >
                    {node.attemptCount !== null && node.attemptCount > 0
                      ? `${node.attemptCount} contact ${
                          node.attemptCount === 1 ? 'attempt' : 'attempts'
                        } logged`
                      : 'Advances automatically when a call is logged'}
                  </span>
                ) : node.active ? (
                  <span className="lead360-stagerail__meta">Current stage</span>
                ) : null}
              </li>
            ))}
          </ol>
          {derivedMilestone}
          <p
            className="lead360-stagerail__terminal"
            data-testid="lead360-stagerail-terminal"
            data-state={model.terminalNode.state}
            data-active={model.terminalNode.active ? 'yes' : 'no'}
          >
            <span className="lead360-stagerail__terminal-label">
              {model.terminalNode.label}
            </span>
            <span className="lead360-muted">
              {model.isLost
                ? 'This lead is lost (terminal). Re-engagement re-enters via a new appointment, not a backward stage move.'
                : 'Terminal disposition -- reachable from any stage; not reached.'}
            </span>
          </p>
        </>
      )}
    </section>
  );
}

/**
 * DEPRECATED (RECORD-360-WORKSPACE-V1, V1-P5): the next action's ONE home is
 * now the current-state band (record/RecordHeaderBand.tsx CurrentStateBand,
 * fed by resolveLeadRecordCurrentTruth) -- this card is NO LONGER COMPOSED on
 * any page. It is retained ONLY because lead360-stage-rail.test.tsx pins its
 * (older, active-appointment-only) derivation as the engine-mirror record;
 * the V7-P4 design pass sweeps it with those pins. Do NOT re-consume it: its
 * logic predates the contract and can disagree with the band post-fulfilment.
 *
 * (Original doc: LIFECYCLE-RENDER-V1 (V1-P2) + TRUTH-DISPLAY LAW
 * (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 5) -- active-appointment overlay,
 * else the engine per-stage contract via the pinned mirror.)
 */
export function NextActionCard({ record }: { record: Lead360Input }): JSX.Element {
  const model = buildStageRailModel(record.stage, record.events);
  const milestone = deriveAppointmentMilestone(record.appointmentSection, Date.now());
  // Only let the appointment fact drive the next action while the RECORDED stage
  // has not yet caught up to appointment_booked (the same guard the derived
  // milestone uses on the rail). Once the stage is at/after appointment_booked,
  // the engine per-stage next action is the accurate one - so an arrived /
  // patient lead is never told to "Confirm the appointment".
  const stageCaughtUp =
    model.currentStage !== null &&
    LEAD_STAGE_RANK[model.currentStage] >= LEAD_STAGE_RANK.appointment_booked;
  const derived = stageCaughtUp ? null : appointmentDerivedNextAction(milestone);
  const nextAction = derived ?? model.nextAction;
  return (
    <section className="lead360-section lead360-nextaction" aria-label="Next action">
      <h4 className="lead360-section__title">
        <SectionIcon name="activity" />
        Next action
      </h4>
      {nextAction === null ? (
        <p className="lead360-muted" data-testid="lead360-nextaction-empty">
          {NEXT_ACTION_UNKNOWN_LABEL}
        </p>
      ) : (
        <div
          className="lead360-nextaction__body"
          data-testid="lead360-nextaction"
          data-stage={model.currentStage ?? ''}
          data-derived-from={derived !== null ? 'appointment' : 'stage'}
        >
          {model.currentLabel !== null ? (
            <p className="lead360-nextaction__stage">
              Current stage: <strong>{model.currentLabel}</strong>
            </p>
          ) : null}
          <p
            className="lead360-nextaction__action"
            data-testid="lead360-nextaction-text"
          >
            {nextAction}
          </p>
          <p className="lead360-muted lead360-nextaction__note">
            {derived !== null
              ? 'Derived from the booked appointment; the recorded stage is unchanged.'
              : 'Read-only view; recording actions arrives with the workflow packages.'}
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * LEAD-360-CONFORMANCE-B1 -- the masthead's whole-record action home
 * (IMPLEMENTATION-CONFORMANCE-CONTRACT.md "Overflow action menu | new
 * OverflowMenu composition over EXISTING writers"). Convert to patient
 * scrolls to the EXISTING patient-link control (no new writer); the two
 * closure dispositions POST the EXISTING, already-role-fenced
 * `recordPrevisitDisposition` endpoint (proven elsewhere on
 * lead-cockpit/CallcenterCommandCenter.tsx) behind a confirm step; Edit
 * details renders the amber DESIGN-ONLY tag and is INERT (F2 entry
 * condition 5(f): no lead-details editor ships without a separate founder
 * functional approval + a FILE-ONLY migration proposal for the audited
 * correction event).
 */
const CLOSURE_REASONS: ReadonlyArray<{
  readonly reasonId: 'not_reachable' | 'duplicate';
  readonly label: string;
}> = [
  { reasonId: 'not_reachable', label: 'Close - not reachable' },
  { reasonId: 'duplicate', label: 'Close as duplicate' },
];

function RecordOverflowMenu({
  record,
  canClose,
  canLogActivity = false,
  onLogActivity,
}: {
  record: Lead360Input;
  /** Gated on the SAME signal buildLeadActionBar already uses for the disposition-writer capability. */
  canClose: boolean;
  /** LEAD-CONTACT-FLOW-V1 (W-28): "Log activity" demoted here as "Add
   *  note / record offline touch" -- invokes the SAME in-band composer the
   *  deleted NEXT-band door reached (no writer moved, only the trigger).
   *  Gated on the same composer-content signal (LES-016: no dead doors). */
  canLogActivity?: boolean;
  onLogActivity?: () => void;
}): JSX.Element {
  const [pending, setPending] = useState<
    (typeof CLOSURE_REASONS)[number] | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  /* LEAD360-B2-G5-POLISH-AND-CLOSURE G5-1 (founder-banked ruling
     CONVERSION-IS-CONSEQUENCE): lead-to-patient conversion happens ONLY as
     a system consequence of Start Consultation -- no manual convert
     action on any surface. The "Convert to patient" overflow item, its
     invoked ModalShell, and the ConvertLeadToPatientControl wiring are
     DELETED (not hidden, not gated to zero callers) -- Lead 360 shows
     conversion STATUS only (the rail's Patient record section). */
  const items: OverflowMenuItem[] = [];
  if (canLogActivity && onLogActivity !== undefined) {
    items.push({
      key: 'add_note',
      label: 'Add note / record offline touch',
      separatorBefore: false,
      onSelect: onLogActivity,
      testId: 'lead-overflow-add-note',
    });
  }
  if (canClose) {
    CLOSURE_REASONS.forEach((reason) => {
      items.push({
        key: reason.reasonId,
        label: reason.label,
        separatorBefore: false,
        onSelect: (): void => {
          setMessage(null);
          setPending(reason);
        },
        testId: `lead-overflow-${reason.reasonId}`,
      });
    });
  }
  items.push({
    key: 'edit_details',
    label: 'Edit details',
    designOnly: true,
    separatorBefore: canClose,
    testId: 'lead-overflow-edit-details',
  });

  return (
    <span className="lead-record__overflow">
      <OverflowMenu
        items={items}
        triggerLabel="Record actions"
        testId="lead-record-overflow"
      />
      {pending !== null ? (
        <ConfirmDialog
          title={pending.label}
          confirmLabel={pending.label}
          busyLabel="Saving..."
          destructive
          busy={busy}
          onCancel={(): void => setPending(null)}
          onConfirm={(): void => {
            setBusy(true);
            recordPrevisitDisposition({
              leadId: Number(record.safeLeadId),
              reasonId: pending.reasonId,
            })
              .then(() => {
                setMessage(`${pending.label} recorded.`);
                setPending(null);
              })
              .catch((e: unknown) => {
                setMessage(
                  e instanceof Error
                    ? e.message
                    : 'Could not record this closure. Please try again.',
                );
              })
              .finally(() => setBusy(false));
          }}
          testId="lead-overflow-confirm"
          confirmTestId="lead-overflow-confirm-confirm"
          cancelTestId="lead-overflow-confirm-cancel"
        >
          <p>
            This records a lawful pre-visit closure for Lead #
            {record.safeLeadId}. The server enforces the writer role and the
            closure precondition.
          </p>
        </ConfirmDialog>
      ) : null}
      {message !== null ? (
        <p className="lead360-muted" data-testid="lead-overflow-message">
          {message}
        </p>
      ) : null}
    </span>
  );
}

export interface LeadDetailContentProps {
  records: readonly Lead360Input[];
  safeLeadId: string;
  /**
   * Back-link target. Defaults to the owner Leads list (the owner kill-switch
   * render); the canonical page passes `/dashboard/leads`.
   */
  backHref?: string;
  /**
   * LEADS-WORKSPACE-ROLE-SCOPED-01 (legacy): historically gated the
   * polished reveal-block spacing. LEAD-CONTACT-FLOW-V1 removed the
   * masthead reveal control, so this prop is now ACCEPTED-AND-INERT --
   * kept so existing hosts/fixtures that pass it stay source-compatible;
   * no render branches on it.
   */
  polished?: boolean;
  /**
   * S2-P2C appointment workbench (DETAIL only). Built by the self-fetching
   * page for the RESOLVED record (numeric leadId + appointmentSection + a
   * refresh callback), gated by role + the /me workbench flag. OMITTED in
   * tests / when disabled => no workbench, byte-identical render.
   */
  renderAppointmentControl?: (record: Lead360Input) => ReactNode;
  /**
   * LEAD-ASSIGNMENT-OWNERSHIP-V1 owner CONTROLS (DETAIL only). Built by the
   * live page for the RESOLVED record (claim / assign / release handlers +
   * the assignable-staff loader + `me` + a refresh callback), rendered ONLY
   * when the record carries `ownerAssignment` AND `me.leadAssignmentEnabled`.
   * OMITTED in tests / SSR / when disabled => the Owner ROW still renders
   * (from `record.ownerAssignment`) but the interactive controls are absent,
   * so the pure body stays byte-identical when the flag is off. Mirrors the
   * `renderAppointmentControl` threading pattern.
   */
  renderOwnerControl?: (record: Lead360Input) => ReactNode;
  /**
   * CORE-DEMO-JOURNEY-STITCH-V1 (Standard 3, journey continuity): whether the
   * viewer's role can lawfully OPEN the linked patient record. Defaults FALSE
   * so the pure SSR tests + the dedup byte-parity fixtures stay byte-identical
   * (the linked-record chip renders disabled-with-reason, never absent); the
   * live workspace page passes `me.patientMasterEnabled`. UX gating only --
   * the SELECT-level fence remains the read boundary; never a widen.
   */
  canOpenPatient?: boolean;
  /**
   * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: the compact disposition
   * panel + the last-contact-attempt summary, injected by the live page for
   * the RESOLVED record (the disposition vocabulary + a save handler + a
   * refresh callback). OMITTED in tests / when disabled => neither renders,
   * byte-identical body. Mirrors the renderOwnerControl threading pattern.
   * LEAD-CONTACT-FLOW-V1: the optional opts carry the panel heading -- the
   * NEXT-card workflow's advance step asks the founder-worded question
   * through it; the overflow-invoked composer keeps the default.
   */
  renderDispositionPanel?: (
    record: Lead360Input,
    opts?: { readonly heading?: string },
  ) => ReactNode;
  /**
   * LEAD-CONTACT-FLOW-V1 (RA-2 re-doored): bumped by the host after a
   * disposition save whose next action is book_appointment -- the record
   * opens its BOOKING MODAL (the one booking door) once bumped, when the
   * canonical rule still permits booking. 0/omitted = no pending chain.
   */
  bookingChainSignal?: number;
  renderLastContactAttempt?: (record: Lead360Input) => ReactNode;
  /**
   * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 2): whether the
   * caller's role holds the ASSIGN-OTHERS door (LeadOwnerControls
   * canAssign(me) -- manager tiers only). Gates ONLY the header action-bar
   * "Reassign" anchor; the ownership controls themselves stay authority-
   * shaped by the existing component. Default FALSE (tests / read-only
   * hosts): no Reassign action renders.
   */
  canReassign?: boolean;
  /**
   * LEAD-RECORD-POLISH-BATCH-V1 (CC-11): `me.leadAssignmentMode` -- the org's
   * CONFIGURED lead-routing mode as the server resolved it onto /me. Feeds the
   * unclaimed seat's self-explanation ("Waiting in the <team> queue - ..."),
   * which claims a reason ONLY for a mode it is given. OMITTED (tests / SSR /
   * a caller outside the assignment audience) => the seat states the queue and
   * nothing more -- never an invented reason.
   */
  assignmentMode?: string | null;
  /**
   * Injected clock. The record derives several time-relative states (the
   * waiting age, upcoming/overdue, the appointment-past next action) and this
   * is the seam that makes them DETERMINISTIC for tests and SSR. OMITTED =>
   * `Date.now()`, i.e. byte-identical to the live page.
   */
  nowMs?: number;
  /**
   * OMNICHANNEL-TIMELINE-AND-LEAD-360-V1: the WINDOWED timeline wire state,
   * injected by the live page (GET /api/leads/:id/timeline -- newest-first
   * entries + honest window disclosure + the notes seam state). OMITTED
   * (tests / SSR / the owner kill-switch render) => the spine renders from
   * the record's OWN events (reversed to newest-first -- the same server
   * merge, same determinism) with no window metadata and no composer: the
   * pure body stays fetch-free.
   */
  timeline?: LeadRecordTimelineState;
  /**
   * The composer's ADD A NOTE control (LeadNoteComposer), injected by the
   * live page for the RESOLVED record (save handler + refresh callback +
   * the notes-store seam state). OMITTED => absent (tests / read-only
   * hosts / roles outside the action audience -- LES-016: no dead doors).
   * LOG A CALL stays the EXISTING renderDispositionPanel above.
   */
  renderNoteComposer?: (record: Lead360Input) => ReactNode;
}

/** The injected windowed-timeline wire state (the live page's fetch). */
export interface LeadRecordTimelineState {
  readonly entries: readonly Lead360Event[];
  readonly status: 'loading' | 'error' | 'ready';
  readonly totalCount: number;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onLoadMore?: () => void;
  readonly onRetry?: () => void;
  readonly eventStreamStatus: Lead360EventStreamStatus;
  readonly notesStreamStatus: 'available' | 'unavailable';
  /**
   * OMNICHANNEL-CONVERSATION-SPINE-V1: the current episode's stored
   * conversation off the SAME timeline read (readConversationFromTimeline).
   * OMITTED (undefined), null (the wire carried no leg) and status
   * 'unavailable' (the store is not applied yet) all leave the lane
   * byte-identical to before (the mount gate in the center slot); only a
   * LIVE store ('available') draws the transcript block.
   */
  readonly conversation?: LeadConversationView | null;
}

/**
 * Presentational body (no fetch) -- rendered directly in tests via
 * react-dom/server (wrapped in a Router for the back link). Renders the
 * single resolved lead record, or an honest "not found in this clinic"
 * state. THIS is the canonical Lead 360 detail body (V1-P3): the full-page
 * route and the owner kill-switch render compose it identically.
 *
 * LEAD-360-CONFORMANCE-B2: carries ONE local `useState` (the composer's
 * invocation flag, COMPOSER ON INVOCATION) -- a static react-dom/server
 * render still renders its collapsed INITIAL value cleanly (no effects, no
 * hydration assumed), the same way ui/OverflowMenu's own local state does
 * in this repo's pinned static-render test idiom.
 */
export function LeadDetailContent({
  records,
  safeLeadId,
  backHref = BACK_TO_LEADS,
  renderAppointmentControl,
  renderOwnerControl,
  canOpenPatient = false,
  renderDispositionPanel,
  renderLastContactAttempt,
  canReassign = false,
  assignmentMode,
  nowMs: nowMsProp,
  timeline,
  renderNoteComposer,
  bookingChainSignal,
}: LeadDetailContentProps): JSX.Element {
  const record = findLeadRecord(records, safeLeadId);

  if (record === null) {
    return (
      <section className="lead-detail lead-record--b">
        <div className="page-header">
          <h1>{formatLeadRef(safeLeadId)}</h1>
          <p className="page-header__sub">Lead record</p>
        </div>
        {/* V7-P4A: the bare .empty-state div becomes the canonical
            EmptyState -- same honest copy, same test id. */}
        <EmptyState
          testId="lead-detail-not-found"
          title="This lead was not found in the current clinic scope."
          body="It may belong to a different clinic - open it from the Leads list for that clinic."
        />
        <p className="mt-4">
          <BackLink to={backHref} />
        </p>
      </section>
    );
  }

  // RECORD-360-WORKSPACE-V1 (V1-P5): THE current-truth contract resolved
  // ONCE for the whole workspace -- the header, the band, and every zone
  // speak from this single resolution (the drawer resolves the SAME
  // contract over the SAME read, so the two surfaces cannot disagree).
  const nowMs = nowMsProp ?? Date.now();
  const truth = resolveLeadRecordCurrentTruth(record, nowMs);
  const view = buildLead360View(record);
  const sections = view.visibleSections;
  const lastAttempt = resolveLastContactAttempt(record);
  const linkedRecords = leadLinkedRecords(truth, canOpenPatient);
  const detailFields = buildLeadDetailFields({ record, view, truth, lastAttempt });
  // LEAD-360-CONFORMANCE-B2: the pack's rail map (F2 region 7) relocates the
  // SAME attribution field set into "Campaign and source" in the RAIL -- the
  // Details zone keeps only Contact + Lifecycle (the reference facts with no
  // other on-screen home; W1 repetition-kill unchanged, nothing re-derived).
  const detailGroups = groupLeadDetailFields(detailFields);
  const attributionGroup = detailGroups.find((g) => g.key === 'attribution') ?? null;
  const leftDetailGroups = detailGroups.filter((g) => g.key !== 'attribution');
  // LEAD-360-CONFORMANCE-B2: the rail's NEW "Lead details" section (F1 TIER
  // 3 "LEAD DETAILS SECTION") -- a DIFFERENT field set from
  // buildLeadDetailFields above (identity/ownership/lifecycle, not
  // contact-timing/attribution).
  const railDetailFields = buildLeadDetailsRailFields({ view, truth });
  // LEAD-360-CONFORMANCE-B2 (Ruling 1 -- the SAY-ONCE RIDER supersedes the
  // "owner never renders twice" pin FOR THIS PAGE ONLY, per the founder
  // ruling banked in the B2 run record): the masthead's summary strip
  // retires below (showSummaryStrip=false); Waiting and the ownership-plane
  // note FOLD INTO the rail's Owner section rather than being dropped -- the
  // SAME ONE builder, called once more here, never a second derivation.
  const ownershipPlaneRow = resolveOwnershipPlaneRow(record, view);
  const summaryTiles = buildLeadSummaryTiles({
    record,
    view,
    truth,
    ownershipPlaneRow,
    assignmentMode,
    nowMs,
  });
  const waitingTile = summaryTiles.find((t) => t.key === 'waiting') ?? null;
  // CC-11: the same "why is this unclaimed" explanation the tile's `sub`
  // carried (resolveUnclaimedRouting, via the SAME ONE builder) -- folds
  // into the rail's Owner section alongside the pool chip.
  const claimedByTile = summaryTiles.find((t) => t.key === 'claimed_by') ?? null;
  // The 'team' tile carries its OWN role gate internally
  // (buildLeadSummaryTiles only pushes it when isLead360SectionVisible(...,
  // 'nextActionOwner') AND ownershipPlaneRow !== null) -- reusing the TILE
  // (not ownershipPlaneRow directly) means the rail's note inherits the
  // SAME gate the masthead strip enforced, never a second, divergent check.
  const teamTile = summaryTiles.find((t) => t.key === 'team') ?? null;

  // LEAD-CONTACT-FLOW-V1: the 'contact' section flag is no longer consulted
  // here -- the standalone reveal control it gated is retired; the contact
  // act is action-gated (renderDispositionPanel presence) inside the NEXT
  // card instead.
  const showAppointments = isLead360SectionVisible(sections, 'appointment');
  const showActivity = isLead360SectionVisible(sections, 'activity');

  // LEAD-CONTACT-FLOW-V1 -- BOOKING ELIGIBILITY, composed from the two
  // EXISTING canonical rules (never a new frontend state list -- the
  // W-15/W-17 drift class this program exists to kill):
  //   1. the role capability: renderAppointmentControl is injected by the
  //      host ONLY when the server-derived /me flag says so
  //      (me.crmAppointmentWorkbenchEnabled -- resolved through the SAME
  //      authority union the booking write route enforces, api/router.ts);
  //   2. the lead-plane rule: findActiveAppointmentCard (the workbench's
  //      own pinned derivation of the writer's one-active-per-lead
  //      precondition -- an active appointment shows Manage, never Book).
  // Lead STAGE is deliberately NOT consulted: the canonical writer has no
  // stage precondition (a lost lead's re-engagement booking is lawful).
  const activeAppointmentCard = findActiveAppointmentCard(
    record.appointmentSection,
  );
  const workbenchAvailable =
    showAppointments && renderAppointmentControl !== undefined;
  const canBookAppointment =
    workbenchAvailable && activeAppointmentCard === null;
  const hasActiveAppointment =
    workbenchAvailable && activeAppointmentCard !== null;

  // Mission 2 -- the role-shaped action bar: entries reach controls that
  // are ACTUALLY rendered for this caller (LES-016; no dead doors, no new
  // writer, no navigation).
  const actions = buildLeadActionBar({
    hasContactWorkflow: renderDispositionPanel !== undefined,
    canBookAppointment,
    hasActiveAppointment,
    hasOwnershipControls:
      renderOwnerControl !== undefined && record.ownerAssignment !== undefined,
    canReassign,
  });
  // CC-03-lite (LEAD-RECORD-POLISH-BATCH-V1): ONE emphasized action among the
  // header buttons, DERIVED from lifecycle state (the current-truth contract's
  // next lawful action names the plane that owns the next step). A spotlight,
  // not a redesign: same set, same order, same anchors, same role shaping --
  // the emphasis simply stops being hard-coded to "Log outcome".
  const emphasisKey = resolveEmphasisActionKey({
    actions,
    nextActionSource: truth.nextLawfulAction.source,
  });
  // LEAD-360-CONFORMANCE-B1: hoisted so the NEXT band's state chip and the
  // overdue seam read the SAME derivation (one call, never two disagreeing
  // computations -- the X1 law this page already keeps for the rail).
  const upcoming = buildUpcomingItems(truth, lastAttempt, nowMs);
  // LEAD-360-CONFORMANCE-B2: the pack's OVERDUE seam (F2 per-state ATTENTION
  // contract) sits ABOVE the NEXT band -- the SAME overdue derivation the
  // (now-residual) band already carried, never recomputed.
  const overdueItems = upcoming.filter((u) => u.overdue);
  const emphasizedAction =
    actions.find((a) => a.key === emphasisKey) ?? null;
  const nextUpcoming = upcoming.find((u) => !u.overdue) ?? null;

  /* LEAD-360-CONFORMANCE-B2 (Ruling 2 -- ONE-ACTIVE-CONTRACT): the composed
     pack is now this page's ONLY active anatomy contract. Its "old summary
     block" (Visit outcome / Payment / Patient record / Treatment follow-up
     / CRM stage + the band's own duplicate "Next action" line) is DELETED
     by omitting every chip and the next-action line below -- the NEXT band
     above is the ONE loud sentence (F1 TIER 1 "NEXT-LINE DOMINANCE"). The
     residual band renders ONLY what the frozen frames draw no home for:
     the historicalWarnings strip and the desk-closed visit note (neither
     fires on any of the pack's frozen fixtures, so this costs zero pixels
     against the Visual Acceptance Gate) -- never silently dropped either. */
  const ALL_BAND_CHIPS: readonly BandChipKey[] = [
    'appointment',
    'conversion',
    'payment',
    'identity',
    'followup',
    'sla',
    'stage',
  ];

  // LEAD-CONTACT-FLOW-V1 -- THE NEXT-CARD CONTACT WORKFLOW (founder ruling
  // 2026-08-08; WALK W-27/W-28): the contact act lives INSIDE the NEXT
  // band. Clicking a channel fires the ONE server action (permission check
  // + the ONE CONTACT_REVEAL audit row + reveal + SERVER-issued grant
  // expiry from org config); the card then advances in place to the
  // disposition step. Re-masking (expiry or manual close) is a DISPLAY act
  // through the pure model -- the plaintext is dropped from state, no
  // second call, no second audit row.
  const [contactState, setContactState] = useState<ContactWorkflowUiState>(
    CONTACT_WORKFLOW_IDLE,
  );
  const startContact = (channel: ContactChannelChoice): void => {
    setContactState(contactStateStarting(channel));
    startLeadContactWorkflow(String(record.safeLeadId), channel)
      .then((payload) => setContactState(contactStateAfterStart(payload)))
      .catch((err: unknown) => {
        const status =
          err !== null &&
          typeof err === 'object' &&
          'status' in err &&
          typeof (err as { status: unknown }).status === 'number'
            ? (err as { status: number }).status
            : null;
        setContactState(contactStateAfterError(status));
      });
  };
  const workflowActive = contactWorkflowInvoked(contactState);

  // LEAD-360-CONFORMANCE-B2 -- COMPOSER ON INVOCATION (F2 region 5 / F1 TIER
  // 2 "COMPOSER ON INVOCATION"): collapsed by default, INSIDE the NEXT
  // band's own card (chronicle.html .edge .composer). LEAD-CONTACT-FLOW-V1
  // re-doors the invocation: the adjacent "Log activity" door is DELETED
  // (W-28 -- a near-identical verb beside the contact act); the composer
  // now opens EITHER as the contact workflow's advance step (the chooser
  // was clicked) OR from the overflow menu's "Add note / record offline
  // touch" item. The disposition panel renders ONCE per mode (a second
  // live mount would be the DUPLICATE-WRITER defect class, LAW 5).
  const [composerInvoked, setComposerInvoked] = useState(false);
  const hasComposerContent =
    renderDispositionPanel !== undefined || renderNoteComposer !== undefined;
  const composerContent = workflowActive ? (
    <div className="ac-nextedge__composer-body">
      <ContactWorkflowPanelView
        state={contactState}
        dispositionSlot={
          renderDispositionPanel !== undefined
            ? renderDispositionPanel(record, { heading: ADVANCE_HEADING })
            : undefined
        }
        onCloseReveal={(): void =>
          setContactState((s) => contactStateMasked(s))
        }
        onRevealAgain={(channel): void => startContact(channel)}
        onGrantExpired={(): void =>
          setContactState((s) => contactStateMasked(s))
        }
        {...(nowMsProp !== undefined ? { nowMs: nowMsProp } : {})}
      />
    </div>
  ) : hasComposerContent ? (
    <div className="ac-nextedge__composer-body">
      {renderDispositionPanel ? renderDispositionPanel(record) : null}
      {renderNoteComposer ? renderNoteComposer(record) : null}
    </div>
  ) : undefined;

  // LEAD-360-CONFORMANCE-B2 G1 (masthead action-button row corrective, CITED
  // authority: chronicle-actions-1440x900 "Composed - Action home
  // (overflow)"): that frame's masthead `mh-act` renders ONLY the overflow
  // dots -- no visible multi-button row. The old Mission-2 visible action
  // bar is GONE (its container, never its behavior): the emphasized action
  // now rides the NEXT band's own primary slot below (same key, same
  // label), every non-emphasized action's control is already a PERMANENT,
  // always-visible element (the masthead Reveal button, the "Log activity"
  // door, the rail's Appointments/Owner sections) rather than a buried
  // scroll target -- so no action loses reachability, and none needed a
  // NEW overflow item beyond what this menu already carried.
  // LEAD360-B2-G5-POLISH-AND-CLOSURE G5-1 (founder-banked ruling
  // CONVERSION-IS-CONSEQUENCE): "Convert to patient" is DELETED from this
  // menu -- lead-to-patient conversion happens ONLY as a system consequence
  // of Start Consultation, never a manual action on any surface. The
  // menu's real paths are now the two closures + the design-only Edit
  // details tag; the rail's Patient record section carries conversion
  // STATUS only (never a writer).
  // LEAD-CONTACT-FLOW-V1 (W-28 heal): "Log activity" demotes into the
  // overflow menu as "Add note / record offline touch" -- never adjacent
  // to NEXT. It invokes the SAME in-band composer (disposition + note) the
  // deleted door reached; no writer moved, only the trigger.
  const overflowMenu = (
    <RecordOverflowMenu
      record={record}
      canClose={renderDispositionPanel !== undefined}
      canLogActivity={hasComposerContent}
      onLogActivity={(): void => setComposerInvoked(true)}
    />
  );

  // LEAD-CONTACT-FLOW-V1 (W-27 heal, founder ruling 2026-08-08): the
  // masthead's separate "Reveal" text link is DELETED -- the founder never
  // registered it as pressable, and it was disconnected from the contact
  // act. The audited reveal now happens INSIDE the NEXT card's contact
  // workflow (the ONE server action; same audit posture, same policy home
  // lib/contact-reveal-policy.ts). The masthead keeps its PASSIVE
  // availability fact ("Phone on file") -- a fact, not a door. The
  // LeadContactRevealControl component itself is untouched (its other
  // adopters -- lead inbox, org intake, check-in identity -- keep it).

  // LEAD-CONTACT-FLOW-V1 (W-29 heal) -- THE BOOKING MODAL: the one booking
  // door, opened from the persistent header action (and the NEXT band's
  // book emphasis, and the post-disposition chain). Hosts the EXISTING
  // workbench render-prop (the canonical writer's own UI) -- zero new
  // booking logic. EXCLUSIVE MOUNT: while the modal is open the rail
  // renders no workbench (one live mount of the booking writer's UI,
  // never two -- the DUPLICATE-WRITER class).
  const [bookingOpen, setBookingOpen] = useState(false);
  useEffect(() => {
    if ((bookingChainSignal ?? 0) > 0 && canBookAppointment) {
      setBookingOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingChainSignal]);

  // LEAD-360-CONFORMANCE-B2 / CHRONICLE-KIT-EXTRACTION-V1: the frozen
  // RECORD-PAGE anatomy is now ASSEMBLED from the named AEDS kit
  // (frontend/src/dashboard/kit) -- a byte-identical render, the extracted
  // parts replacing the former inline composition (07-BEHAVIOURAL-LAWS.md
  // "### ARCH" UPDATE 2 + "### KIT"). Each slot below carries the SAME markup
  // the inline version emitted; only the container components moved.
  const kitTop = (
    <p className="lead-detail__back">
      {/* Standard 5 (record-anatomy header grammar): the shared breadcrumb
          trail gives a lawful back-path (Leads -> this lead). Rendered ABOVE
          the record360 header so it never disturbs the dedup byte-parity
          window (which starts at record360-header). */}
      <Breadcrumbs
        items={leadRecordBreadcrumbs(record.safeLeadId, { leadsHref: backHref })}
        testId="lead360-breadcrumbs"
      />
      <BackLink to={backHref} />
    </p>
  );

  // 1. COMPACT RECORD HEADER + 2. STATUS PATH BAR -- KitRecordMasthead carries
  // identity + chips + one-line meta only (F2 region 3; the finding-4
  // ownership planes' summary strip stays RETIRED via the kit's own
  // showSummaryStrip=false default -- LEAD-360-CONFORMANCE-B2, Ruling 1) and
  // mounts the stage rail (LeadStagePathBar -- the existing lifecycle truth,
  // read-only) directly beneath. LEAD-CONTACT-FLOW-V1 (W-29, founder ruling
  // 2026-08-08): the actions slot now carries the PERSISTENT "Book
  // appointment" action, rendered EXACTLY when the canonical rule permits
  // booking, beside the whole-record overflow menu. SECONDARY weight by
  // design: the NEXT band owns the page's ONE primary (UX LAW 4 / DENSITY
  // rule 8 hold -- when NEXT itself says Book, the band carries the
  // primary and this header door stays the quiet any-moment sibling of
  // the SAME modal). An ineligible state renders NO bookable action. The
  // meta line's reveal slot is gone (W-27): no revealControl is passed,
  // so the masthead renders the passive availability fact only.
  const kitMasthead = (
    <KitRecordMasthead
      record={record}
      truth={truth}
      actions={
        <>
          {canBookAppointment ? (
            <SecondaryAction
              type="button"
              data-testid="lead-header-book-appointment"
              onClick={(): void => setBookingOpen(true)}
            >
              Book appointment
            </SecondaryAction>
          ) : null}
          {overflowMenu}
        </>
      }
      assignmentMode={assignmentMode}
      nowMs={nowMs}
      stageRail={<LeadStagePathBar record={record} />}
    />
  );

  // OVERDUE seam (aboveNext slot) -- LEAD-360-CONFORMANCE-B2 (F2 per-state ATTENTION
  // contract: "OVERDUE seam ... ABOVE the NEXT band") -- the SAME overdue
  // derivation the residual band below no longer carries (moved, not
  // recomputed; the markup + test ids are byte-identical to the band's own
  // alert block, LEAD-RECORD-POLISH-BATCH-V1 X1).
  const kitAboveNext =
    overdueItems.length > 0 ? (
      <div className="record360-band__alerts" data-testid="record360-band-alerts">
        {overdueItems.map((u) => (
          <span
            key={u.key}
            className="record360-band__alert"
            data-testid={`record360-alert-${u.key}`}
          >
            Overdue - {u.label}
            {u.dueLabel !== null ? (
              <span className="record360-band__alert-due">
                was due {u.dueLabel}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    ) : null;

  // NEXT band (next slot) -- LEAD-360-CONFORMANCE-B1/B2 anatomy, re-doored
  // by LEAD-CONTACT-FLOW-V1 (NEXT-IS-EXECUTABLE, founder ruling
  // 2026-08-08): the band LAUNCHES the workflow it names, in place.
  //   - contact_lead emphasis -> the channel chooser (Phone primary /
  //     WhatsApp quiet) rides the actions row; a click fires the ONE
  //     server action and the card advances in place (reveal + countdown
  //     -> disposition) inside the composer strip;
  //   - book_appointment emphasis -> opens the record's BOOKING MODAL
  //     (the one booking door -- no rail anchor, no navigation);
  //   - manage_appointment / reassign -> anchor to their PERMANENT rail
  //     sections (the ARCH anchor clause -- the control's home is an
  //     always-visible rail section).
  // The old "Log outcome" primary and the adjacent "Log activity" door are
  // DELETED (W-28); the composer opens from the workflow or the overflow.
  const kitNext = (
    <KitNextBand
      testId="lead-nextedge"
      sentence={truth.nextLawfulAction.label}
      stateChip={
        nextUpcoming !== null && nextUpcoming.dueLabel !== null ? (
          <span data-testid="lead-nextedge-state">
            {nextUpcoming.label} - Due {nextUpcoming.dueLabel}
          </span>
        ) : undefined
      }
      primary={
        emphasisKey === 'contact_lead' ? (
          <ContactWorkflowChooser
            pending={contactState.kind === 'starting'}
            onChoose={startContact}
          />
        ) : emphasizedAction !== null ? (
          emphasisKey === 'book_appointment' ? (
            <PrimaryAction
              type="button"
              data-testid="lead-nextedge-primary"
              onClick={(): void => setBookingOpen(true)}
            >
              {emphasizedAction.label}
            </PrimaryAction>
          ) : (
            <a
              href={`#${emphasizedAction.targetId}`}
              className="ac-btn ac-btn--primary"
              data-testid="lead-nextedge-primary"
            >
              {emphasizedAction.label}
            </a>
          )
        ) : undefined
      }
      composer={composerContent}
      invoked={workflowActive || composerInvoked}
    />
  );

  // 3. THE RESIDUAL STATUS STRIP (belowNext slot) -- LEAD-360-CONFORMANCE-B2
  // (Ruling 2, ONE-ACTIVE-CONTRACT): the old summary block's chips and its
  // duplicate "Next action" line are DELETED (every chip omitted;
  // omitNextAction) -- the NEXT band above is the ONE loud sentence. Only
  // historicalWarnings + the desk-closed visit note can render here; neither
  // fires on the pack's frozen fixtures (zero pixel cost). LEAD360-B2-G5-
  // POLISH-AND-CLOSURE M5 REJECTED deleting this shell outright: the e2e spec
  // pins `record360-band` as a permanently-visible structural landmark.
  const kitBelowNext = (
    <CurrentStateBand truth={truth} omitChips={ALL_BAND_CHIPS} omitNextAction />
  );

  // 4. THE TIMELINE-DOMINANT CENTER (center slot) -- KitTimeline promotes the
  // heart now; its card-in-a-stack chrome (the lead360-section border + the
  // "Activity" h4 title) is RETIRED -- the KitTimeline primitive's OWN card
  // treatment (dashboard.css .ac-timeline) carries the visual weight
  // chronicle-normal's `.feed` draws, with filter grammar + latest-accent +
  // routine-dimming inherited from the primitive. Every OTHER legacy card
  // (Contact/Details/Related/Lead History) is dispositioned BY NAME in the
  // LEAD-360-CONFORMANCE-B2 run record -- relocated to the masthead meta line,
  // the rail, or the timeline's own per-entry detail disclosure, or retired
  // with its pins (R-015). buildLeadTimelineEntries stays here (a page-specific
  // adapter mapping the lead's lawful read onto TimelineEntry[]); the kit
  // renders it, never builds it.
  const kitCenter = showActivity
    ? (() => {
        const streamStatus =
          timeline?.eventStreamStatus ?? view.eventStreamStatus;
        const wireEntries =
          timeline !== undefined
            ? timeline.entries
            : [...view.events].reverse();
        const entries = buildLeadTimelineEntries(
          wireEntries,
          record.sourceNormalized ?? null,
          nowMs,
        );
        // OMNICHANNEL-CONVERSATION-SPINE-V1: the TRANSCRIPT rides the same
        // timeline lane, ABOVE the activity feed -- the lane that used to
        // show the red error box for an org-plane lead now shows the
        // patient's own words first, then the activity chronicle. MOUNT
        // GATE: drawn ONLY when the wire carried the leg AND the store is
        // LIVE (status 'available' -- the FILE-ONLY migration applied). A
        // host that passes no leg, an older wire (null), and a dark store
        // ('unavailable') all render this lane BYTE-IDENTICALLY to before,
        // so the frozen Lead 360 anatomy the visual acceptance gate diffs
        // against (docs/AGENT-RUNS/lead-360-conformance-b2-evidence) holds
        // until real conversation data exists. Once the store is live a
        // lead with no stored messages gets the transcript's connected
        // zero state (never a red box, never blank); the seam copy stays
        // in the component for its own states and is pinned there.
        const transcript =
          timeline !== undefined &&
          timeline.conversation !== undefined &&
          timeline.conversation !== null &&
          timeline.conversation.status === 'available' ? (
            <LeadConversationTranscript
              conversation={timeline.conversation}
              status={timeline.status}
              {...(timeline.onRetry !== undefined ? { onRetry: timeline.onRetry } : {})}
            />
          ) : null;
        return (
          <>
          {transcript}
          <KitTimeline
            entries={entries}
            status={timeline?.status ?? 'ready'}
            ariaLabel="Lead activity timeline"
            emptyTitle={
              streamStatus === 'available'
                ? 'Activity stream connected - no activity recorded yet.'
                : 'Activity stream not wired yet.'
            }
            emptyTestId={
              streamStatus === 'available'
                ? 'lead360-activity-empty-wired'
                : 'lead360-activity-not-wired'
            }
            totalCount={timeline?.totalCount ?? entries.length}
            hasMore={timeline?.hasMore ?? false}
            {...(timeline?.onLoadMore !== undefined
              ? { onLoadMore: timeline.onLoadMore }
              : {})}
            loadingMore={timeline?.loadingMore ?? false}
            {...(timeline?.onRetry !== undefined
              ? { onRetry: timeline.onRetry }
              : {})}
            testId="lead-timeline"
          />
          </>
        );
      })()
    : null;

  // THE CONTEXT RAIL (rail slot): the activity FEED moved to the center spine
  // (SAY IT ONCE -- record360-zone-timeline is its one home); the rail carries
  // the composed related-list sections. LEAD-360-CONFORMANCE-B2 (F2 region 7
  // "Rail: related-list sections in state order"): every section below is a
  // RELOCATION of an existing element (never a rebuild), wrapped in the
  // KitRailSection signature (counted expand-in-place over the ratified #478
  // SectionContainer). Only "Lead details" is new content. The per-state ORDER
  // stays this surface's OWN call (cited from its approved pack), never the kit's.
  const kitRail = (() => {
          const olderHistoryCount =
            timeline !== undefined && timeline.hasMore
              ? Math.max(timeline.totalCount - timeline.entries.length, 0)
              : 0;
          const appointmentsCount = record.appointmentSection?.cards.length ?? 0;
          const hasCurrentAppointment = truth.currentAppointment.kind !== 'none';

          const olderHistorySection =
            olderHistoryCount > 0 ? (
              <KitRailSection
                key="older-history"
                title="Older history"
                count={olderHistoryCount}
                testId="lead-rail-older-history"
              >
                <p className="lead360-muted">Expands in the timeline below.</p>
              </KitRailSection>
            ) : null;

          {/* LEAD-360-CONFORMANCE-B2 G1: the Owner section's render gate
              WIDENS to cover its new relocated content (a lead can carry an
              SLA breach or a last-contact-attempt truth with no owner
              assignment at all) -- `leftDetailGroups` is the Lifecycle
              field group, which `buildLeadDetailFields` always populates
              (Last contact + Callback due are unconditional pushes), so
              this condition is effectively always true; spelled out fully
              rather than collapsed to `true` so a future field-set change
              cannot silently make the section appear empty. */}
          const ownerSectionVisible =
            ownershipPlaneRow !== null ||
            record.ownerAssignment !== undefined ||
            (record.slaState !== undefined && record.slaState.breaches.length > 0) ||
            renderLastContactAttempt !== undefined ||
            leftDetailGroups.length > 0;

          const ownerSection =
            ownerSectionVisible ? (
              <KitRailSection
                key="owner"
                title="Owner"
                count={1}
                defaultOpen
                testId="lead-rail-owner"
              >
                {/* LEAD-360-CONFORMANCE-B2 (Ruling 1 -- Waiting/Claimed fold
                    into the rail's Owner section): the SAME ownership-plane
                    note the masthead strip used to carry, from the SAME
                    tile (buildLeadSummaryTiles) -- never a second
                    derivation AND never a second, divergent role gate. The
                    'team' tile is role-gated INTERNALLY (isLead360SectionVisible
                    'nextActionOwner'); reusing it here (not ownershipPlaneRow
                    directly) means a front_desk/call_center_agent caller
                    that must never see this plane gets NOTHING, exactly as
                    the masthead strip enforced (a LAW 2/LAW 3 proof, not a
                    UI-only hide). */}
                {teamTile !== null && ownershipPlaneRow !== null ? (
                  <p
                    className="lead-record-related__emptyline"
                    data-testid="lead-rail-owner-team-note"
                  >
                    <span className="lead-record-related__title">
                      {teamTile.label}
                    </span>{' '}
                    <span className="lead360-muted">
                      {/* Legacy test id RIDES the value to its new home
                          (the codebase's established migration idiom, per
                          buildLeadSummaryTiles's own doc comment) -- the
                          fact moved, it was not renamed or duplicated. */}
                      <span
                        data-testid="record360-next-action-owner"
                        data-plane={teamTile.plane}
                      >
                        {teamTile.value}
                      </span>{' '}
                      - {teamTile.sub}
                    </span>
                  </p>
                ) : null}
                {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M4 -- FOUND CONFLICT,
                    RESOLVED CONSERVATIVELY (recorded for founder
                    adjudication in the stop report): M4's own text says
                    Owner carries ONLY the chip + Release action, with
                    Waiting moving to Lead details. But this exact fact
                    (`lead-rail-owner-waiting`) is ALSO one of the FIVE-
                    SECOND LAW's named always-visible answers (07-
                    BEHAVIOURAL-LAWS.md; enforced by THIS spec's own
                    `alwaysVisibleInsideDetails` check + the fold-answers
                    loop) -- it must never sit inside a closed disclosure,
                    and Lead details defaults closed. Moving it there broke
                    that pre-existing, standing product law (confirmed via
                    a real test failure, not a theory). Waiting STAYS here
                    in Owner (which stays defaultOpen) so the older,
                    product-wide law wins over this package's narrower
                    "Owner = chip + Release ONLY" geometry goal; SLA,
                    last-contact-attempt, and the Lifecycle field group
                    (none of which the Five-Second Law names) still MOVE to
                    Lead details, exactly as M4 asks. */}
                {waitingTile !== null ? (
                  <p
                    className="lead-record-related__emptyline"
                    data-testid="lead-rail-owner-waiting"
                  >
                    <span className="lead-record-related__title">
                      {waitingTile.label}
                    </span>{' '}
                    <span className="lead360-muted">
                      {waitingTile.value}
                      {waitingTile.sub !== null ? ` - ${waitingTile.sub}` : ''}
                    </span>
                  </p>
                ) : null}
                {/* CC-11: the unclaimed seat explains itself from REAL
                    routing state (resolveUnclaimedRouting, via the tile's
                    own sub) -- renders only for a POOL lead (the sub is
                    null once assigned, since escalation/routing reasons
                    stop applying). */}
                {claimedByTile !== null && claimedByTile.sub !== null ? (
                  <p
                    className="lead-record-related__emptyline"
                    data-testid="lead-rail-owner-routing-note"
                  >
                    <span className="lead360-muted">{claimedByTile.sub}</span>
                  </p>
                ) : null}
                {record.ownerAssignment !== undefined ? (
                  <div id={ACTION_TARGET_OWNERSHIP} data-testid="lead-record-ownership">
                    {renderOwnerControl ? (
                      renderOwnerControl(record)
                    ) : (
                      <LeadOwnerControlsView
                        ownership={record.ownerAssignment}
                        renderChrome={false}
                        me={null}
                        staff={[]}
                        staffLoading={false}
                        submitting={false}
                        message={null}
                      />
                    )}
                  </div>
                ) : null}
                {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M4 disposition
                    (SUPERSEDES the G1 disposition above that parked this
                    content here): waiting/SLA, last-contact-attempt, and
                    the Lifecycle field group all MOVED to the Lead details
                    section below -- chronicle-normal.png's Owner section
                    carries ONLY the owner chip + Release action, at target
                    compactness. See leadDetailsSection for the relocated
                    render (byte-identical content, new home). */}
              </KitRailSection>
            ) : null;

          const leadDetailsSection = (
            <KitRailSection
              key="lead-details"
              title="Lead details"
              count={railDetailFields.length}
              testId="lead-rail-details"
            >
              <FieldGrid
                label="Lead details"
                fields={railDetailFields.map((f) => ({
                  label: f.label,
                  value: f.value ?? undefined,
                  missingLabel: f.missingLabel,
                  testId: f.testId,
                }))}
              />
              {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M4 (rail geometry):
                  SLA, last-contact-attempt, and the Lifecycle field group
                  MOVE here from the Owner section (SUPERSEDES the G1
                  disposition that parked them there) -- collapsed by
                  default, counted (unchanged: the count above tracks only
                  `railDetailFields`, the C-8 enumerable-evidence rule --
                  these are supplementary body content, not new count items,
                  exactly as the OLD Owner section's hardcoded count={1}
                  never counted its own supplementary content either).
                  Every render below is byte-identical to its old Owner
                  render -- relocated, never rebuilt. Waiting does NOT move
                  here -- see the Owner section's own comment for the FOUND
                  CONFLICT with the standing FIVE-SECOND LAW that keeps it
                  in Owner instead. */}
              {renderLastContactAttempt ? renderLastContactAttempt(record) : null}
              {leftDetailGroups.map((group) => (
                <div
                  key={group.key}
                  className="lead-record-details__group"
                  data-testid={`lead-details-group-${group.key}`}
                >
                  <span className="lead-record-details__group-title">
                    {group.title}
                  </span>
                  <FieldGrid
                    label={`Lead details - ${group.title}`}
                    fields={group.fields.map((f) => ({
                      label: f.label,
                      value: f.value ?? undefined,
                      missingLabel: f.missingLabel,
                      testId: f.testId,
                    }))}
                  />
                </div>
              ))}
              <SlaBreachPanel record={record} />
            </KitRailSection>
          );

          const campaignSourceSection =
            attributionGroup !== null && attributionGroup.fields.length > 0 ? (
              <KitRailSection
                key="campaign-source"
                title="Campaign and source"
                count={attributionGroup.fields.length}
                testId="lead-details-attribution-params"
              >
                <FieldGrid
                  label="Lead details - Attribution"
                  fields={attributionGroup.fields.map((f) => ({
                    label: f.label,
                    value: f.value ?? undefined,
                    missingLabel: f.missingLabel,
                    testId: f.testId,
                  }))}
                />
              </KitRailSection>
            ) : null;

          const appointmentsSection = showAppointments ? (
            <KitRailSection
              key="appointments"
              title="Appointments"
              count={appointmentsCount}
              defaultOpen={appointmentsCount > 0}
              testId="record360-zone-appointments"
              stat={<AppointmentStatusChip truth={truth} />}
            >
              <div id={ACTION_TARGET_APPOINTMENTS}>
                {/* LEAD-CONTACT-FLOW-V1 (W-29): the rail is the appointments
                    DISPLAY home; BOOKING's only door is the header modal.
                    The workbench mounts here ONLY when an ACTIVE appointment
                    exists (its pinned behavior then shows Manage, never
                    Book -- so no second booking door can render) and never
                    while the modal is open (one live mount of the booking
                    writer's UI). A bookable lead renders NO workbench here:
                    AppointmentZone displays the cards; the header action
                    carries the act. */}
                {hasActiveAppointment && !bookingOpen && renderAppointmentControl
                  ? renderAppointmentControl(record)
                  : null}
                <AppointmentZone section={record.appointmentSection} />
                {(() => {
                  /* V1-P6 evidence-rows law: the payment EVIDENCE ROWS stay
                     reachable from this SAME section (the anchor id is
                     unchanged, so any existing #record360-zone-payments
                     link still lands). DENSITY: an empty payments zone
                     renders as ONE honest line, never a heading + empty
                     list + note stacked into a full empty card. */
                  const noPayments =
                    truth.currentPaymentState.kind === 'none' ||
                    truth.currentPaymentState.kind === 'unknown_on_this_read';
                  return (
                    <div
                      className={
                        noPayments
                          ? 'lead-record-related__block lead-record-related__block--empty'
                          : 'lead-record-related__block'
                      }
                      id="record360-zone-payments"
                      data-testid="record360-zone-payments"
                    >
                      {noPayments ? (
                        <p
                          className="lead-record-related__emptyline"
                          data-testid="record360-payments-empty"
                        >
                          <span className="lead-record-related__title">Payments</span>{' '}
                          <span className="lead360-muted">
                            No payment evidence rows on this record.
                          </span>
                        </p>
                      ) : (
                        <>
                          <h5 className="lead-record-related__title">Payments</h5>
                          <PaymentRelatedList section={record.appointmentSection} />
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </KitRailSection>
          ) : null;

          {/* LEAD-360-CONFORMANCE-B2 G1 disposition: ReturningPatientPanel
              (the CONTACT card's phone-match recognition) MERGES into this
              section rather than the main column -- it and the linked-
              record display are MUTUALLY EXCLUSIVE per lead (a confirmed
              link supersedes an unconfirmed phone-match observation), so
              one section, one state, never both at once. */}
          const patientRecordSection =
            linkedRecords.length > 0 || record.returningVisitor !== undefined ? (
              <KitRailSection
                key="patient-record"
                title="Patient record"
                count={linkedRecords.length > 0 ? linkedRecords.length : 1}
                defaultOpen
                testId="lead-rail-patient-record"
              >
                {linkedRecords.length > 0 ? (
                  <div className="record360-linked" data-testid="record360-linked-records">
                    {linkedRecords.map((lr) =>
                      lr.href !== null ? (
                        <Link
                          key={lr.key}
                          to={lr.href}
                          className="ac-btn ac-btn--secondary ac-btn--sm"
                          data-testid={lr.testId}
                        >
                          Open {lr.label}
                        </Link>
                      ) : (
                        <span
                          key={lr.key}
                          className="ac-btn ac-btn--secondary ac-btn--sm record360-linked__disabled"
                          aria-disabled="true"
                          data-testid={lr.testId}
                          title={lr.disabledReason ?? undefined}
                        >
                          {lr.label} - {lr.disabledReason}
                        </span>
                      ),
                    )}
                  </div>
                ) : (
                  <ReturningPatientPanel record={record} />
                )}
              </KitRailSection>
            ) : null;

          /* F2 per-state rail order (NORMAL / DENSE / CONVERTED all cited
             verbatim): CONVERTED leads (a linked patient record exists)
             lead with the evidence rail (Patient record + Appointments,
             the CONVERTED eye path's "evidence rail" -- F1 PER-STATE EYE
             PATHS); an open/current appointment otherwise outranks
             Campaign-and-source (the DENSE ordering); the NORMAL order
             (Owner / Lead details / Campaign and source / Appointments)
             is the default. */
          const sections =
            linkedRecords.length > 0
              ? [patientRecordSection, appointmentsSection, ownerSection, leadDetailsSection, campaignSourceSection]
              : hasCurrentAppointment
                ? [ownerSection, leadDetailsSection, appointmentsSection, campaignSourceSection]
                : [ownerSection, leadDetailsSection, campaignSourceSection, appointmentsSection];

          return (
            <>
              {olderHistorySection}
              {sections}
            </>
          );
  })();

  // ASSEMBLE the frozen RECORD-PAGE anatomy from the kit (D2 zero-change
  // re-point): masthead / lifecycle / NEXT band / timeline-dominant center /
  // counted rail, in the archetype's fixed order, with the frozen column
  // shares. KitRecordPage renders the outer scope section, the two-column
  // grid, the center zones container, and the rail <aside> -- every class name,
  // test id, and child order is byte-identical to the former inline shell.
  // LEAD-CONTACT-FLOW-V1: the BOOKING MODAL (S-1 focused work = centered
  // ModalShell) renders OVER the record as a sibling -- no navigation. It
  // hosts the EXISTING workbench render-prop (the canonical booking
  // writer's own UI, verbatim; zero new booking logic). Post-book, the
  // workbench's own inline success + the record's re-derivation carry the
  // consequence; the operator closes the modal (ONE dismissal truth).
  return (
    <>
      <KitRecordPage
        className="lead-detail lead-record lead-record--b"
        top={kitTop}
        masthead={kitMasthead}
        aboveNext={kitAboveNext}
        next={kitNext}
        belowNext={kitBelowNext}
        center={kitCenter}
        rail={kitRail}
      />
      {bookingOpen && renderAppointmentControl !== undefined ? (
        <ModalShell
          title="Book appointment"
          size="lg"
          testId="lead-booking-modal"
          onClose={(): void => setBookingOpen(false)}
        >
          {renderAppointmentControl(record)}
        </ModalShell>
      ) : null}
    </>
  );
}
